// composables/useFocusTrap.ts
// Keyboard focus containment for dialogs (WCAG 2.4.3 Focus Order / 2.1.2 No Keyboard Trap
// in the *intended* sense: Tab cycles inside the open dialog and never leaks to the page
// behind it). Also restores focus to the element that opened the dialog on close.
//
// Stack-aware: when dialogs are nested (BaseModal → ConfirmModal), only the top-most
// active trap handles Tab, so the inner dialog is never hijacked by the outer one.
//
// Usage:
//   const panelRef = ref<HTMLElement | null>(null)
//   useFocusTrap(panelRef, () => props.modelValue)
//
// Options:
//   initialFocus — CSS selector to focus on open; `false` = don't move focus (caller does
//                  it); default = the container itself (needs tabindex="-1").
//   restoreFocus — return focus to the previously focused element on close (default true).
import { watch, nextTick, onBeforeUnmount, type Ref } from 'vue'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

interface FocusTrapOptions {
  initialFocus?: string | false
  restoreFocus?: boolean
}

// Module-level stack of active traps; top of stack owns the Tab key.
const activeTraps: symbol[] = []

export function useFocusTrap(
  container: Ref<HTMLElement | null | undefined>,
  active: () => boolean,
  opts: FocusTrapOptions = {},
) {
  const id = Symbol('focus-trap')
  let previouslyFocused: HTMLElement | null = null
  let listening = false

  const isTop = () => activeTraps[activeTraps.length - 1] === id

  function focusables(): HTMLElement[] {
    const root = container.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter(el => el.getClientRects().length > 0 && el.getAttribute('aria-hidden') !== 'true')
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !isTop() || !active()) return
    const root = container.value
    if (!root) return
    const els = focusables()
    if (!els.length) { e.preventDefault(); root.focus({ preventScroll: true }); return }
    const first = els[0]
    const last  = els[els.length - 1]
    const current = document.activeElement as HTMLElement | null
    const inside = !!current && root.contains(current)
    if (e.shiftKey) {
      if (!inside || current === first || current === root) { e.preventDefault(); last.focus() }
    } else if (!inside || current === last) {
      e.preventDefault(); first.focus()
    }
  }

  function activate() {
    if (typeof document === 'undefined' || listening) return
    previouslyFocused = document.activeElement as HTMLElement | null
    activeTraps.push(id)
    document.addEventListener('keydown', onKeydown, true)
    listening = true
    if (opts.initialFocus === false) return
    nextTick(() => {
      const root = container.value
      if (!root || !active()) return
      const target = (opts.initialFocus && root.querySelector<HTMLElement>(opts.initialFocus)) || root
      target.focus({ preventScroll: true })
    })
  }

  function deactivate() {
    if (typeof document === 'undefined' || !listening) return
    document.removeEventListener('keydown', onKeydown, true)
    listening = false
    const i = activeTraps.indexOf(id)
    if (i !== -1) activeTraps.splice(i, 1)
    const prev = previouslyFocused
    previouslyFocused = null
    if (opts.restoreFocus !== false && prev && document.contains(prev) && typeof prev.focus === 'function') {
      prev.focus({ preventScroll: true })
    }
  }

  watch(active, (on) => { on ? activate() : deactivate() }, { immediate: true, flush: 'post' })
  onBeforeUnmount(deactivate)

  return { activate, deactivate }
}
